// src/utils/domIds.js
//
// Convención de identificadores del DOM
// =====================================
// Objetivo: poder señalar sin ambigüedad qué bloque de la interfaz se está
// mirando —en una consola, en un reporte de bug, en una captura— y tener un
// punto de anclaje estable para tests, analítica o ajustes futuros.
//
// FORMATO
//   {ámbito}-{bloque}[-{elemento}][-{identificador}]
//
//   ámbito         app | header | nav | page-<pagina> | <componente-compartido>
//   bloque         zona funcional dentro del ámbito (hero, filtros, grid…)
//   elemento       pieza concreta del bloque (input, boton, panel…)
//   identificador  solo cuando el elemento se repite (id de producto, slug…)
//
// EJEMPLOS
//   page-exclusivos                     raíz de la página
//   page-exclusivos-filtros-panel       panel emergente de filtros
//   page-exclusivos-grid                grilla de resultados
//   product-card-1892                   una card concreta
//   departamentos-nav-exclusivos        el carrusel, en su uso de Exclusivos
//
// REGLAS
//  1. kebab-case, sin acentos ni mayúsculas. Usar `slugId()` para texto que
//     venga de datos (nombres de departamento, tiendas, categorías).
//  2. Un id es único en el documento. Todo lo que se renderice dentro de un
//     `.map()` DEBE incluir un identificador propio de la entidad; si no, se
//     generan duplicados y `document.getElementById` devuelve el primero,
//     silenciosamente.
//  3. Los componentes reutilizables reciben un `idPrefix` desde quien los usa,
//     porque el mismo componente puede aparecer en más de una página y sus ids
//     tienen que decir en cuál está.
//  4. Los ids son contrato de lectura, no de estilo: no se usan como selectores
//     CSS. Renombrar uno no debe cambiar la apariencia de nada.

// Rango Unicode de marcas diacríticas combinadas (U+0300–U+036F). Se filtran
// por código y no con una clase de caracteres literal, que es frágil ante
// cambios de encoding del archivo.
const DIACRITICO_MIN = 0x300;
const DIACRITICO_MAX = 0x36f;

/**
 * Normaliza texto arbitrario a un fragmento de id seguro.
 * 'Hogar y Muebles' → 'hogar-y-muebles' · 'Tecnología' → 'tecnologia'
 */
export const slugId = (texto) => {
  if (texto === null || texto === undefined) return 'sin-valor';

  // NFD separa la letra de su acento; después se descarta el acento, de modo
  // que 'ó' termina en 'o' y no en un guion.
  const sinAcentos = Array.from(String(texto).normalize('NFD'))
    .filter(ch => {
      const cp = ch.codePointAt(0);
      return cp < DIACRITICO_MIN || cp > DIACRITICO_MAX;
    })
    .join('');

  return sinAcentos
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')   // todo lo no alfanumérico → guion
    .replace(/^-+|-+$/g, '')       // sin guiones al borde
    || 'sin-valor';
};

/**
 * Une fragmentos en un id, descartando los vacíos.
 * domId('page-exclusivos', 'card', 1892) → 'page-exclusivos-card-1892'
 *
 * Los números pasan tal cual (son identificadores de entidad); el texto se
 * normaliza con slugId().
 */
export const domId = (...partes) =>
  partes
    .filter(p => p !== null && p !== undefined && p !== '')
    .map(p => (typeof p === 'number' ? String(p) : slugId(p)))
    .join('-');
